import React, { useState, useEffect, useRef } from "react";
import "./Chatbot.css";
import { useAskChatBotMutation } from "features/script/api";
import MessageFormatter from "./MessageFormatter";
import { MdOutlineCleaningServices } from "react-icons/md";

const ChatWindow = ({ onClose }) => {
  const [messages, setMessages] = useState([
    { from: "bot", type: "welcome", text: "Hi! How can I help you today?" },
  ]);

  const [askChatbot, { isLoading: loading }] = useAskChatBotMutation();

  const [userInput, setUserInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const chatBodyRef = useRef(null);
  const textareaRef = useRef(null);

  //  added for session persistence — load chat from localStorage
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const savedChat = localStorage.getItem(`chat_${token}`);
      if (savedChat) {
        setMessages(JSON.parse(savedChat));
      }
    }
  }, []);

  //  added for session persistence — save chat to localStorage on update
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      localStorage.setItem(`chat_${token}`, JSON.stringify(messages));
    }
  }, [messages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Auto-resize textarea based on content
  const handleInputChange = (e) => {
    setUserInput(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120; // Max 5 lines approximately
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    }
  };

  // Clear chat functionality
  const handleClearChat = () => {
    setShowClearConfirm(true);
  };

  const confirmClearChat = () => {
    // Reset to initial welcome message
    setMessages([
      { from: "bot", type: "welcome", text: "Hi! How can I help you today?" },
    ]);
    
    // Clear from localStorage
    const token = localStorage.getItem("token");
    if (token) {
      localStorage.removeItem(`chat_${token}`);
    }
    
    // Clear current input
    setUserInput("");
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    setShowClearConfirm(false);
  };

  const cancelClearChat = () => {
    setShowClearConfirm(false);
  };

  //  Function to handle sending messages
  const handleSend = async () => {
    if (!userInput.trim()) return; // prevent empty messages

    const userMessage = { from: "user", text: userInput };
    // Remove welcome message when user starts chatting
    setMessages((prev) => {
      const filteredMessages = prev.filter(msg => msg.type !== 'welcome');
      return [...filteredMessages, userMessage];
    });
    setUserInput("");
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    setIsTyping(true);

    try {
      const res = await askChatbot({ text: userMessage.text });
      const botReply =
        res?.data?.reply || "⚠️ Sorry, I couldn’t process that message.";

      setMessages((prev) => [...prev, { from: "bot", text: botReply }]);
    } catch (error) {
      console.error("Error communicating with backend:", error);
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "⚠️ Something went wrong connecting to the server." },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  //  Send message on Enter key press (Shift+Enter for new line)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent default Enter behavior
      handleSend();
    }
    // Shift+Enter will create a new line (default textarea behavior)
  };

  return (
    <>
      {showClearConfirm && (
        <div className="clear-confirm-overlay">
          <div className="clear-confirm-modal">
            <div className="confirm-header">
              <h3>Clear Chat History</h3>
            </div>
            <div className="confirm-body">
              <p>Are you sure you want to clear all chat messages? This action cannot be undone.</p>
            </div>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={cancelClearChat}>
                Cancel
              </button>
              <button className="confirm-clear" onClick={confirmClearChat}>
                Clear Chat
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="chat-window">
        <div className="chat-header">
        <h4>ILLNESSRX Chatbot</h4>
        <div className="header-buttons">
          {/* Only show clear button if there are messages beyond the welcome message */}
          {messages.length > 1 || (messages.length === 1 && messages[0].type !== 'welcome') ? (
            <button className="clear-btn" onClick={handleClearChat} title="Clear Chat">
              <MdOutlineCleaningServices size={18} />
            </button>
          ) : null}
          <button className="close-btn" onClick={onClose} title="Close Chat">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="chat-body" ref={chatBodyRef}>
        {messages.map((msg, i) => (
          <div key={i} className={`chat-message ${msg.from} ${msg.type === 'welcome' ? 'welcome-message' : ''}`}>
            {msg.type === 'welcome' ? (
              <div className="welcome-container">
                <div className="chatbot-avatar">
                  <div className="avatar-circle">
                    <div className="avatar-person">
                      <div className="person-head"></div>
                      <div className="person-body"></div>
                    </div>
                    <div className="speech-bubble">
                      <div className="bubble-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="welcome-text">
                  <h3>Hi! How can I help you today?</h3>
                  <div className="prompt-suggestions">
                    <div className="prompt-item" onClick={() => setUserInput("What are the symptoms of diabetes?")}>
                      💊 What are the symptoms of diabetes?
                    </div>
                    <div className="prompt-item" onClick={() => setUserInput("How to manage high blood pressure?")}>
                      🩺 How to manage high blood pressure?
                    </div>
                    <div className="prompt-item" onClick={() => setUserInput("Explain my medication side effects")}>
                      📋 Explain my medication side effects
                    </div>
                    <div className="prompt-item" onClick={() => setUserInput("What should I know about my upcoming surgery?")}>
                      🏥 What should I know about my upcoming surgery?
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              msg.from === 'bot' ? (
                <MessageFormatter text={msg.text} />
              ) : (
                msg.text
              )
            )}
          </div>
        ))}
        {isTyping && (
          <div className="typing-indicator">
            <div className="typing-dots">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          </div>
        )}
      </div>

      <div className="chat-footer">
        <textarea
          ref={textareaRef}
          value={userInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message... (Shift+Enter for new line)"
          rows={1}
          style={{ resize: 'none' }}
        />
        <button onClick={handleSend} disabled={loading || !userInput.trim()}>
          {loading ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3"/>
              <path d="M12 2C17.5228 2 22 6.47715 22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="1s" repeatCount="indefinite"/>
              </path>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
            </svg>
          )}
        </button>
      </div>
    </div>
    </>
  );
};


export default ChatWindow;